import { isBookingProcessAlias } from '../transactions/transaction';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from './types';

export const WATCHED_LISTING_IDS_KEY = 'watchedListingIds';
export const MAX_WATCHED_LISTINGS = 100;

export const WATCH_STATUS_AVAILABLE = 'available';
export const WATCH_STATUS_SOLD_OUT = 'soldOut';
export const WATCH_STATUS_UNAVAILABLE = 'unavailable';
export const WATCH_STATUS_REMOVED = 'removed';

export const WATCH_LIMIT_REACHED = 'WATCH_LIMIT_REACHED';

/**
 * Normalize a Sharetribe id (UUID object or string) to a string.
 * @param {string|{uuid: string}|null|undefined} id
 * @returns {string|null}
 */
export const toListingIdString = id => {
  if (!id) {
    return null;
  }
  if (typeof id === 'string') {
    return id;
  }
  return id.uuid || null;
};

/**
 * Normalize a privateData watch entry (legacy string id or snapshot object).
 * @param {string|Object|null|undefined} entry
 * @returns {{ id: string, title: string|null, imageUrl: string|null, price: {amount: number, currency: string}|null, watchedAt: string|null }|null}
 */
export const normalizeWatchedEntry = entry => {
  if (!entry) {
    return null;
  }
  if (typeof entry === 'string') {
    return {
      id: entry,
      title: null,
      imageUrl: null,
      price: null,
      watchedAt: null,
    };
  }
  const id = toListingIdString(entry.id);
  if (!id) {
    return null;
  }
  const amount = coerceMoneyAmount(entry.price?.amount);
  const price =
    amount != null && typeof entry.price?.currency === 'string'
      ? { amount, currency: entry.price.currency }
      : null;

  return {
    id,
    title: typeof entry.title === 'string' ? entry.title : null,
    imageUrl: sanitizeWatchImageUrl(typeof entry.imageUrl === 'string' ? entry.imageUrl : null),
    price,
    watchedAt: typeof entry.watchedAt === 'string' ? entry.watchedAt : null,
  };
};

/**
 * @param {Object|null|undefined} currentUser
 * @returns {Array<{ id: string, title: string|null, imageUrl: string|null, price: Object|null, watchedAt: string|null }>}
 */
export const getWatchedListingEntries = currentUser => {
  const raw = currentUser?.attributes?.profile?.privateData?.[WATCHED_LISTING_IDS_KEY];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(normalizeWatchedEntry).filter(Boolean);
};

/**
 * @param {Object|null|undefined} currentUser
 * @returns {string[]}
 */
export const getWatchedListingIds = currentUser => {
  return getWatchedListingEntries(currentUser).map(e => e.id);
};

/**
 * @param {Object|null|undefined} currentUser
 * @param {string|{uuid: string}} listingId
 * @returns {Object|null} normalized snapshot entry
 */
export const getWatchedListingSnapshot = (currentUser, listingId) => {
  const idStr = toListingIdString(listingId);
  if (!idStr) {
    return null;
  }
  return getWatchedListingEntries(currentUser).find(e => e.id === idStr) || null;
};

/**
 * @param {Object|null|undefined} currentUser
 * @param {string|{uuid: string}} listingId
 * @returns {boolean}
 */
export const isListingWatched = (currentUser, listingId) => {
  const idStr = toListingIdString(listingId);
  if (!idStr) {
    return false;
  }
  return getWatchedListingIds(currentUser).includes(idStr);
};

/**
 * Coerce a Sharetribe Money amount to a finite number (handles goog.math.Long).
 * @param {*} rawAmount
 * @returns {number|null}
 */
export const coerceMoneyAmount = rawAmount => {
  if (typeof rawAmount === 'number' && Number.isFinite(rawAmount)) {
    return rawAmount;
  }
  if (rawAmount != null && typeof rawAmount.toString === 'function') {
    const asNumber = Number(rawAmount.toString());
    return Number.isFinite(asNumber) ? asNumber : null;
  }
  return null;
};

/**
 * Only allow http(s) image URLs in snapshots / rendering.
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export const sanitizeWatchImageUrl = url => {
  if (typeof url !== 'string' || !url) {
    return null;
  }
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch (e) {
    return null;
  }
};

/**
 * Pick a stable-enough card image URL from a denormalised listing.
 * @param {Object} listing
 * @param {string} [variantPrefix='listing-card']
 * @returns {string|null}
 */
export const getListingCardImageUrl = (listing, variantPrefix = 'listing-card') => {
  const firstImage = listing?.images?.[0];
  const variants = firstImage?.attributes?.variants;
  if (!variants || typeof variants !== 'object') {
    return null;
  }
  // Prefer standard card size over 2x to keep privateData snapshots smaller.
  const preferred =
    variants[variantPrefix] ||
    variants[`${variantPrefix}-2x`] ||
    Object.values(variants).find(v => v && typeof v.url === 'string');
  return sanitizeWatchImageUrl(preferred?.url || null);
};

/**
 * Build a privateData snapshot for a listing being watched.
 * @param {Object} listing
 * @param {Object} [options]
 * @param {string} [options.variantPrefix]
 * @param {string} [options.watchedAt] ISO timestamp; defaults to now
 * @returns {{ id: string, title: string|null, imageUrl: string|null, price: {amount: number, currency: string}|null, watchedAt: string }|null}
 */
export const createWatchedListingSnapshot = (listing, options = {}) => {
  const id = toListingIdString(listing?.id);
  if (!id) {
    return null;
  }
  const { variantPrefix = 'listing-card', watchedAt = new Date().toISOString() } = options;
  const title = typeof listing?.attributes?.title === 'string' ? listing.attributes.title : null;
  const money = listing?.attributes?.price;
  const amount = coerceMoneyAmount(money?.amount);
  const price =
    amount != null && typeof money?.currency === 'string'
      ? { amount, currency: money.currency }
      : null;

  return {
    id,
    title,
    imageUrl: getListingCardImageUrl(listing, variantPrefix),
    price,
    watchedAt,
  };
};

/**
 * Compute the next watched-entry array after toggling one listing.
 * Newest watched entry is prepended. Preserves existing snapshots when unwatching others.
 *
 * @param {Array<string|Object>} currentEntries
 * @param {string} listingIdStr
 * @param {Object|null} [snapshot] - required when adding; ignored when removing
 * @returns {{ nextEntries: Object[], didWatch: boolean } | { error: string }}
 */
export const computeNextWatchedListings = (currentEntries, listingIdStr, snapshot = null) => {
  const entries = (Array.isArray(currentEntries) ? currentEntries : [])
    .map(normalizeWatchedEntry)
    .filter(Boolean);
  const isWatched = entries.some(e => e.id === listingIdStr);

  if (isWatched) {
    return {
      nextEntries: entries.filter(e => e.id !== listingIdStr),
      didWatch: false,
    };
  }

  if (entries.length >= MAX_WATCHED_LISTINGS) {
    return { error: WATCH_LIMIT_REACHED };
  }

  const nextSnapshot = normalizeWatchedEntry(snapshot) || {
    id: listingIdStr,
    title: null,
    imageUrl: null,
    price: null,
    watchedAt: new Date().toISOString(),
  };

  return {
    nextEntries: [nextSnapshot, ...entries],
    didWatch: true,
  };
};

/**
 * @deprecated Use computeNextWatchedListings. Kept for tests/compat — works with id strings only.
 */
export const computeNextWatchedListingIds = (currentIds, listingIdStr) => {
  const result = computeNextWatchedListings(currentIds, listingIdStr, { id: listingIdStr });
  if (result.error) {
    return result;
  }
  return {
    nextIds: result.nextEntries.map(e => e.id),
    didWatch: result.didWatch,
  };
};

/**
 * Whether a published listing should be treated as sold out for watch UI.
 * Matches Manage Listing out-of-stock rules (stock-tracked purchase listings only).
 *
 * @param {Object} listing
 * @returns {boolean}
 */
export const isSoldOutListing = listing => {
  const state = listing?.attributes?.state;
  const currentStock = listing?.currentStock?.attributes?.quantity;
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  const isBookable = isBookingProcessAlias(processAlias);
  return !isBookable && currentStock === 0 && state === LISTING_STATE_PUBLISHED;
};

/**
 * Classify a watched listing for the Watched Listings page.
 *
 * @param {Object|null|undefined} listing - Denormalised listing or null/undefined if missing
 * @returns {'available'|'soldOut'|'unavailable'|'removed'}
 */
export const classifyWatchedListing = listing => {
  if (!listing?.id) {
    return WATCH_STATUS_REMOVED;
  }

  const state = listing.attributes?.state;
  if (
    state === LISTING_STATE_CLOSED ||
    state === LISTING_STATE_PENDING_APPROVAL ||
    state === LISTING_STATE_DRAFT
  ) {
    return WATCH_STATUS_UNAVAILABLE;
  }

  if (isSoldOutListing(listing)) {
    return WATCH_STATUS_SOLD_OUT;
  }

  return WATCH_STATUS_AVAILABLE;
};
